"use client";

import { getUnixTime } from "date-fns";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

/**
 * The page's single clock, in unix seconds.
 *
 * Every loan display that depends on the current time reads it from here, so a
 * loan's badge, its due note and its overdue banner cannot disagree about
 * whether a deadline has passed. Before this, each of those sampled the time
 * itself on its own interval, which left a window where a row's badge still
 * read "Current" beside a note already reading "overdue".
 *
 * 10s matches the cadence the call sites had already settled on. Everything
 * rendered from it is minute-granular, so a value can be at most 10s late
 * showing a change that only happens once a minute.
 */
const TICK_MS = 10_000;

const NowContext = createContext<number | undefined>(undefined);

export function useNow() {
  const now = useContext(NowContext);
  if (now == undefined) throw new Error("useNow must be called within a NowProvider");
  return now;
}

interface NowProviderProps {
  children: ReactNode;
}

export function NowProvider(props: NowProviderProps) {
  const { children } = props;

  const [now, setNow] = useState(() => getUnixTime(new Date()));

  useEffect(() => {
    const interval = setInterval(() => setNow(getUnixTime(new Date())), TICK_MS);
    return () => clearInterval(interval);
  }, []);

  return <NowContext.Provider value={now}>{children}</NowContext.Provider>;
}
